import React from 'react';
import { FileText } from 'lucide-react';
import { Row, Col, Input, Select, SectionTitle, FormField, FileInput } from '../../../SharedInputs.jsx';

export function SectionVisa({ value, onChange, errors = [], dispatch, category }) {
  const updateField = (field, fieldValue) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const updateFile = (field, file) => {
    dispatch({ type: 'SET_FILE', section: 'visa', field, file });
  };

  const FIELD_LABELS = {
    visaStatus: "Current Visa Status",
    visaType: "Visa Type/Subclass",
    visaExpiryDate: "Visa Expiry Date (if current)",
    appliedBefore: "Has the student applied for a student visa or any other visa to Australia in the past?",
    granted: "Was your previous application granted?",
    refusalDate: "Refusal Date",
    refusalReason: "Reason for Refusal",
    embassyOffice: "Embassy/Department of Home Affairs office where visa will be lodged/was granted",
    currentVisaCopy: "Upload Current/Previous Visa Copy",
    refusalLetter: "Upload Refusal Letter"
  };

  const getFieldError = (fieldName) => {
    return errors.find(error => error.toLowerCase().includes(fieldName.toLowerCase()));
  };
  return (
    <div>
      <SectionTitle icon={FileText} step={4}>
        Current / Previous Visa Status and Immigration History
      </SectionTitle>

      <div style={{
        padding: 16,
        background: '#f8faff',
        borderRadius: 8,
        border: '1px solid #e0e7ff',
        marginBottom: 24
      }}>
        <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
          Please provide information about your current visa status and any previous 
          Australian visa applications. This information helps us understand your 
          immigration history and provide appropriate guidance.
        </p>
      </div>

      {/* Current Visa Status */}
      <Row>
        <Col span={4}>
          <FormField label="Current Visa Status"
          required
          error={getFieldError('current visa status')}
          >
            <Select
              value={value.visaStatus}
              onChange={(e) => updateField('visaStatus', e.target.value)}
              required
              error = {getFieldError('current visa status')}
            >
              {category === "domestic" ? <option value="Select">Select Visa Status</option>
              : <option value="No Visa Yet (Offshore Application)">No Visa Yet (Offshore Application)</option>}
              <option value="Current Student Visa">Current Student Visa</option>
              <option value="Current Tourist Visa">Current Tourist Visa</option>
              <option value="Current Working Holiday Visa">Current Working Holiday Visa</option>
              <option value="Current Temporary Graduate Visa">Current Temporary Graduate Visa</option>
              <option value="Current Other Visa">Current Other Visa</option>
              <option value="Previous Visa (Expired)">Previous Visa (Expired)</option>
            </Select>

          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Visa Type/Subclass"
          error={getFieldError('visa type/ subclass')}
          required={value.visaStatus !== "No Visa Yet (Offshore Application)"}>
            <Input
              value={value.visaType}
              onChange={(e) => updateField('visaType', e.target.value)}
              placeholder="e.g. Student Visa 500, Tourist 600"
              error={getFieldError('visa type/ subclass')}
              required={value.visaStatus !== "No Visa Yet (Offshore Application)"}
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Visa Expiry Date (if current)"
          error={getFieldError('visa expiry date')}
          required={value.visaStatus !== "No Visa Yet (Offshore Application)"}>
            <Input
              type="date"
              value={value.visaExpiryDate}
              onChange={(e) => updateField('visaExpiryDate', e.target.value)}
              required
              error={getFieldError('visa expiry date')}
            />
          </FormField>
        </Col>
      </Row>

      {/* Previous Applications */}
      <Row>
        <Col span={6}>
          <FormField label="Has the student applied for a student visa or any other visa to Australia in the past? "
          required
          error={getFieldError('past visa')}>
          <Select
              value={value.appliedBefore}
              onChange={(e) => updateField('appliedBefore', e.target.value)}
              required
              error={getFieldError('past visa')}  
            >
              <option value="No">No</option>
              <option value="Yes">Yes - for offshore student</option>
            </Select>
          </FormField>
        </Col>
        {value.appliedBefore === "Yes" && (
          <Col span={6}>
            <FormField label="Was your previous application granted?"
            required
            error={getFieldError('Previous application result')}>
              <Select
                value={value.granted}
                onChange={(e) => updateField('granted', e.target.value)}
                required
                error={getFieldError('Previous application result')}
              >
                <option value="">Please select</option>
                <option value="Yes">Yes - Granted</option>
                <option value="No">No - Refused</option>
              </Select>
            </FormField>
          </Col>
        )}
      </Row>

      {/* Refusal Details */}
      {value.granted === "No" && (
        <Row>
          <Col span={4}>
            <FormField label="Refusal Date"
            required
            error={getFieldError('refusal date')}>
              <Input
                type="date"
                value={value.refusalDate}
                onChange={(e) => updateField('refusalDate', e.target.value)}
                required
                error={getFieldError('refusal date')}
              />
            </FormField>
          </Col>
          <Col span={8}>
            <FormField 
              label="Reason for Refusal"
              hint="Please provide brief details of the refusal reason"
              required
              error={getFieldError('refusal reason')}
            >
              <Input
                value={value.refusalReason}
                onChange={(e) => updateField('refusalReason', e.target.value)}
                placeholder="e.g. Insufficient funds, GTE concerns, etc."
                required
                error={getFieldError('refusal reason')}
              />
            </FormField>
          </Col>
        </Row>
      )}

      {/* Embassy/DHA Office */}
      <Row>
        <Col span={12}>
          <FormField 
            label="Embassy/Department of Home Affairs office where visa will be lodged/was granted"
            hint="e.g. Australian Embassy Bangkok, DHA Brisbane, etc."
            error={getFieldError('embassy/dha office')}
          >
            <Input
              value={value.embassyOffice}
              onChange={(e) => updateField('embassyOffice', e.target.value)}
              placeholder="Embassy or DHA office location"
              required
              error={getFieldError('embassy/dha office ')}
            />
          </FormField>
        </Col>
      </Row>

      {/* File Uploads */}
      <Row>
        <Col span={6}>
          <FormField 
            label="Upload Current/Previous Visa Copy"
            hint="If you have a current or previous Australian visa"
            error={getFieldError('current/previous visa')}
          >
            <FileInput
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(file) => updateFile('currentVisaCopy', file)}
              value={value.currentVisaCopy}
              placeholder="Upload visa document"
              required
              error={getFieldError('current/previous visa')}
            />
          </FormField>
        </Col>
        {value.granted === "No" && (
          <Col span={6}>
            <FormField 
              label="Upload Refusal Letter"
              hint="Official refusal letter from DHA"
              required
              error={getFieldError('refusal letter')}
            >
              <FileInput
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(file) => updateFile('refusalLetter', file)}
                value={value.refusalLetter}
                placeholder="Upload refusal letter"
                required
              error={getFieldError('refusal letter')}
              />
            </FormField>
          </Col>
        )}
      </Row>

      {/* Visa Information */}
      <div style={{
        marginTop: 24,
        padding: 16,
        background: '#fffbeb',
        borderRadius: 8,
        border: '1px solid #fed7aa'
      }}>
        <h4 style={{ margin: '0 0 12px', color: '#92400e' }}>Visa Application Information</h4>
        <ul style={{ margin: 0, paddingLeft: 20, color: '#b45309', fontSize: 14 }}>
          <li style={{ marginBottom: 8 }}>International students typically require a Student Visa (subclass 500)</li>
          <li style={{ marginBottom: 8 }}>Visa processing times vary by country and application complexity</li>
          <li style={{ marginBottom: 8 }}>Previous visa refusals don't automatically disqualify you from studying</li>
          <li>Our student support team can provide guidance on visa requirements</li>
        </ul>
      </div>
    </div>
  );
}